import { ChangeDetectionStrategy, Component, Inject, OnInit, inject, DestroyRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { CoreService } from '../../services/core.service';
import { take } from "rxjs/operators";
import { Router, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FacebookPostComponent } from "../../components/templates/facebook-post/facebook-post.component";
import { FacebookStoryComponent } from "../../components/templates/facebook-story/facebook-story.component";
import { InstagramPostComponent } from "../../components/templates/instagram-post/instagram-post.component";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectorRef } from '@angular/core';

import {
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
  MAT_DIALOG_DATA
} from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { AnalyticsComponent } from './analytics/analytics.component';

type Post = {
  id: number;
  rss_title: string;
  domain_name?: string;
  image_url: string;
  post_status?: string;
  enable_repeat?: number;
  parent_post_id?: number;
  tag?: string;
  social_platform_title?: string;
  scheduled_for?: string | Date;
  rss_description?: string;
};

type Alert = {
  id: number;
  contentsite_id: number;
  alert_text: string;
  alert_type: string;
  active: number;
  platform: string;
  format: string;
  rss_img_url: string;
  rss_title: string;
  created_at: string | Date;
  Domain: string;
  posting_queue_id: number;
};



@Component({
  selector: 'app-delete-confirm-dialog',
  standalone: true,
  template: `
    <h2 mat-dialog-title class="text-red-500">Confirm Delete</h2>
    <mat-dialog-content>
      Are you sure you want to delete this post?
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancel</button>
      <button mat-button color="warn" [mat-dialog-close]="'confirm'">Delete</button>
    </mat-dialog-actions>
  `,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButtonModule,
    MatTooltipModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeleteConfirmDialogComponent { }

@Component({
  selector: 'app-preview-test',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,          
    FacebookPostComponent,
    InstagramPostComponent,
    FacebookStoryComponent,
    AnalyticsComponent
  ],
  templateUrl: './preview-test.component.html',
  styleUrl: './preview-test.component.scss',
  changeDetection: ChangeDetectionStrategy.Default
})
export class PreviewTestComponent implements OnInit {
  //analytics 
   viewMode: 'preview' | 'analytics' = 'preview';
  setViewMode(mode: 'preview' | 'analytics') {
    this.viewMode = mode;
  }
  
  private readonly destroyRef = inject(DestroyRef);
  renderPreview = true;

  id: string | null = null;
  nextId: string | null = null;
  prevId: string | null = null;
  tag: string | null = '';
  nextTag: string | null = '';
  prevTag: string | null = '';
  companyID: number = 0;
  postStatus: string = '';
  enableRepeat: number | null = null;
  enableEdit: number = 1;
  enableReuse: number = 1;
  enableDelete: number = 1;

  // sidebar posts
  maxSidebarCards = 3;
  posts: Post[] = [];
    alerts: Alert[] = [];
  naPlaceholder = './assets/images/na.png';
  private readonly LATEST_POSTS_KEY = 'latestPosts';
  private readonly LATEST_ALERTS_KEY = 'latestAlerts';
  private readonly cdr = inject(ChangeDetectorRef);

  private dialog = inject(MatDialog);
  

  constructor(
    private router: Router,
    private location: Location,
    private route: ActivatedRoute,
    private readonly coreService: CoreService,
  ) { }

  ngOnInit() {
    this.companyID = this.coreService.user?.companyID || 0;
    this.loadSidebarPosts();
    this.loadAlertsFromStorage();
    this.route.paramMap
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(pm => {
      this.tag = pm.get('tag');
      this.id = pm.get('id');
      if (this.id) {
        this.loadActions(this.id);
      }
      this.renderPreview = false;
      this.cdr.detectChanges();
      this.renderPreview = true;
    });
  }

  loadActions(id: string): void {
    this.coreService
      .getData(`/feedsync/post/actions/${this.companyID}/${id}`)
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          if (!res) {
            this.coreService.showSnackbar('Post not found.');
            return;
          }

          this.postStatus = res.post_status ?? '';
          this.enableRepeat = typeof res.enable_repeat === 'number'
            ? res.enable_repeat
            : null;

          this.enableEdit = 1;
          this.enableReuse = 1;
          this.enableDelete = 1;

          this.nextId  = res.next_id ?? null;
          this.nextTag = res.next_tag ?? null;

          this.prevId  = res.prev_id ?? null;
          this.prevTag = res.prev_tag ?? null;
        },
        error: (err: any) => {
          console.error('Failed to load post actions:', err);
          this.coreService.showSnackbar('Could not load post actions.');
        }
      });
  }

  get isEditDisabled(): boolean {
    const status = (this.postStatus || '').toLowerCase();
    const repeat = this.enableRepeat ?? 0;

    return (
      status === 'postedoninstagram' ||
      status === 'postedonfacebook' ||
      repeat === -1
    );
  }

  get isReuseDisabled(): boolean {
    const status = (this.postStatus || '').toLowerCase();
    return (
      status === 'draft' ||
      status === 'fail' ||
      status === 'permanently failed'
    );
  }

  get isDeleteDisabled(): boolean {
    const status = (this.postStatus || '').toLowerCase();
    return status !== 'draft';
  }


  refreshPage() {
    window.location.reload();
  }

  editPost(id: any) {
    this.router.navigate(['/manual-post/edit', id]);
  }

  reusePost() {
    this.router.navigate(['/reuse-post', this.id]);
  }

  nextPost() {
    this.router.navigate(['/preview-post/' + this.nextTag + '/' + this.nextId]).then(() => {
      window.location.reload();
    });
  }

  prevPost() {
    this.router.navigate(['/preview-post/' + this.prevTag + '/' + this.prevId]).then(() => {
      window.location.reload();
    });
  }

  viewList() {
    this.router.navigate(['/posts']);
  }

  onMainImgError(ev: Event): void {
    const img = ev.target as HTMLImageElement;
    if (!img || img.src.endsWith(this.naPlaceholder)) return;
    img.src = this.naPlaceholder;
  }

  private loadSidebarPosts(): void {
    if (typeof window === 'undefined') {
      this.posts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_POSTS_KEY);

    if (!raw) {
      this.posts = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Post[] | null;

      if (Array.isArray(parsed)) {
        this.posts = parsed.slice(0, this.maxSidebarCards);
      } else {
        this.posts = [];
      }
    } catch (e) {
      console.error('Failed to parse latest posts from localStorage', e);
      this.posts = [];
    }
  }

    private loadAlertsFromStorage(): void {
    if (typeof window === 'undefined') return;

    const raw = localStorage.getItem(this.LATEST_ALERTS_KEY);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.alerts = parsed.slice(0, this.maxSidebarCards);
      }
    } catch (e) {
      console.error('Failed to parse alerts from storage', e);
    }
  }

  viewAlert(alert: Alert): void {
    const targetId = alert.posting_queue_id;
    this.router.navigate(['/preview-post', alert.format, targetId]);
  }

  viewRow(row: Post) {
    const targetId =
      row.enable_repeat === -1 ? row.parent_post_id ?? row.id : row.id;
    this.router.navigate(['/preview-post', row.tag, targetId]);
  }

  trackById = (_: number, item: Post) => item.id;

    trackByAlertId = (_: number, item: Alert) => item.id;

  openDeleteConfirm() {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: "380px"
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === "confirm" && this.id) {
        this.deletePost();
      }
    });
  }

  deletePost() {
    this.coreService.postData(`manual-post/deletePost/${this.id}`, {
      company_id: this.companyID
    }).subscribe(() => {
      this.router.navigate(['/posts']);
    });
  }

  backToPrevPage() {
    this.location.back();
  }

  platformTextClass(plat?: string) {
    const t = (plat || "").toLowerCase().trim();
    switch (t) {
      case "instagram":
        return "text-fuchsia-600";
      case "facebook":
        return "text-blue-600";
      case "x":
        return "text-neutral-700";
      case "youtube":
        return "text-red-600";
      default:
        return "text-gray-700";
    }
  }
}
